import React, { useEffect, useState } from 'react';
import { GitCompare, Info } from 'lucide-react';
import { AnalyticsPeriod, ResolvedPeriod } from '../PeriodPicker';
import { QualityApi, SnapshotRow } from './qualityApi';

interface SprintReleaseSnapshotProps {
  period: AnalyticsPeriod | null;
  resolvedPeriod: ResolvedPeriod | null;
}

export const SprintReleaseSnapshot: React.FC<SprintReleaseSnapshotProps> = ({
  period,
  resolvedPeriod,
}) => {
  const [rows, setRows] = useState<SnapshotRow[]>([]);
  const [periodLabel, setPeriodLabel] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await QualityApi.getSnapshot({ period, resolvedPeriod });
        if (!cancelled) {
          setRows(res.rows ?? []);
          setPeriodLabel(res.periodLabel ?? '');
        }
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setError(err instanceof Error ? err.message : 'Failed to load snapshot');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  const isRelevant = period?.mode === 'sprint' || period?.mode === 'release';

  if (!isRelevant) {
    return (
      <div className="chart-card">
        <div className="chart-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GitCompare size={18} />
            Sprint / Release snapshot
            <span
              className="stat-hint-icon"
              title="Compare this sprint or release to the previous one. Switch the Period at the top to Sprint or Release mode to see the snapshot."
              aria-label="About snapshot"
            >
              <Info size={14} />
            </span>
          </h3>
          <p>Select a Sprint or Release in the Period bar above to see the snapshot.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <GitCompare size={18} />
          Snapshot: {periodLabel || '—'}
          <span
            className="stat-hint-icon"
            title="Side-by-side with the previous period. Red Δ = regression, green Δ = improvement. Use with Dynamics to check if a bad period is noise or a trend."
            aria-label="About snapshot"
          >
            <Info size={14} />
          </span>
        </h3>
        <p>Δ compared with the previous sprint / release.</p>
      </div>
      {loading ? (
        <p className="quality-breakdown__empty">Loading…</p>
      ) : error ? (
        <p className="quality-breakdown__empty">Failed to load: {error}</p>
      ) : rows.length === 0 ? (
        <p className="quality-breakdown__empty">No data for this period.</p>
      ) : (
        <table className="estimation-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
              <th>Previous</th>
              <th>Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const delta = r.delta ?? 0;
              const isBetter = r.betterIsLower ? delta <= 0 : delta >= 0;
              const deltaClass = delta === 0 ? '' : isBetter ? 'deviation-under' : 'deviation-over';
              return (
                <tr key={r.metric}>
                  <td>{r.metric}</td>
                  <td>{r.value}</td>
                  <td>{r.prevValue ?? '—'}</td>
                  <td>
                    {r.delta == null ? (
                      '—'
                    ) : (
                      <span className={deltaClass}>
                        {delta >= 0 ? '+' : ''}
                        {delta}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
